import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import AdminSelect from '../../../common/AdminSelect';
import { ModalShell } from '../../partners/ReviewActionModals';
import { useCreateEvent, useUpdateEvent } from '../../../../services/leads';

const FIELD_LABEL =
  'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground';

// Mirror of the server's slug normalization (leadsEventsController) so the
// preview is honest about what will actually be created. Underscores collapse
// to dashes too — "go_to-this-link" reads odd.
const normalizeSlug = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// Create / edit an event lead channel. The link (slug) builds live from the
// name and can be adjusted BEFORE creation — after that it's immutable
// (printed QRs). The QR short code is always minted automatically.
const EventModal = ({ event, onClose }) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(event?.partnerId);

  // pg returns date columns as ISO datetimes — date inputs want YYYY-MM-DD.
  const ymd = (v) => (v ? String(v).slice(0, 10) : '');

  const [form, setForm] = useState({
    name: event?.partnerName || '',
    slug: event?.partnerReferralUrl || '',
    details: event?.partnerDetails || '',
    startsAt: ymd(event?.eventStartsAt),
    endsAt: ymd(event?.eventEndsAt),
    includeLogo: Boolean(event?.partnerQrIncludesLogo),
    assignReferralsTo: event?.partnerAssignReferralsTo
      ? {
          label: event.partnerAssignReferralsToName,
          value: event.partnerAssignReferralsTo,
        }
      : null,
  });
  // The link auto-builds from the name until the user edits it by hand.
  const [slugTouched, setSlugTouched] = useState(false);
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const setName = (value) =>
    setForm((f) => ({
      ...f,
      name: value,
      ...(slugTouched || isEdit ? {} : { slug: normalizeSlug(value) }),
    }));

  const onSaved = (msg) => {
    queryClient.invalidateQueries(['leads-events']);
    toast.success(msg);
    onClose();
  };
  const onError = (err) => toast.error(err?.response?.data?.msg || err.message);

  const createEvent = useCreateEvent(() => onSaved('Event created.'), {
    onError,
  });
  const updateEvent = useUpdateEvent(() => onSaved('Event updated.'), {
    onError,
  });
  const isSaving = createEvent.isLoading || updateEvent.isLoading;

  const onSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      details: form.details.trim() || null,
      startsAt: form.startsAt || null,
      endsAt: form.endsAt || null,
      includeLogo: form.includeLogo,
      assignReferralsTo: form.assignReferralsTo
        ? form.assignReferralsTo.value
        : null,
    };
    if (
      payload.startsAt &&
      payload.endsAt &&
      payload.endsAt < payload.startsAt
    ) {
      toast.error('The end date must be on or after the start date.');
      return;
    }
    if (!payload.name) {
      toast.error('Event name is required.');
      return;
    }
    if (isEdit) {
      updateEvent.mutate({ partnerId: event.partnerId, ...payload });
    } else {
      createEvent.mutate({ ...payload, slug: normalizeSlug(form.slug) });
    }
  };

  return (
    <ModalShell title={isEdit ? 'Edit Event' : 'Add Event'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className={FIELD_LABEL}>Event name *</label>
          <Input
            value={form.name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="VFW Expo - Jacksonville"
          />
        </div>

        {!isEdit ? (
          <div>
            <label className={FIELD_LABEL}>
              Event link *{' '}
              <span className="font-normal normal-case tracking-normal text-status-warning-fg">
                can&apos;t be changed after the event is created.
              </span>
            </label>
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setField('slug', e.target.value);
              }}
              onBlur={() => setField('slug', normalizeSlug(form.slug))}
              placeholder="vfw-expo-jacksonville"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {`${window.location.protocol}//${window.location.host}/event/${normalizeSlug(form.slug) || '…'}`}
            </p>
          </div>
        ) : (
          <div>
            <label className={FIELD_LABEL}>Event link</label>
            <p className="text-sm text-muted-foreground">
              /event/{event.partnerReferralUrl}{' '}
              <span className="text-xs">(locked after creation)</span>
            </p>
          </div>
        )}

        <div>
          <label className={FIELD_LABEL}>Event dates *</label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              required
              value={form.startsAt}
              max={form.endsAt || undefined}
              onChange={(e) => setField('startsAt', e.target.value)}
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              required
              value={form.endsAt}
              min={form.startsAt || undefined}
              onChange={(e) => setField('endsAt', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Assign leads to</label>
          <AdminSelect
            value={form.assignReferralsTo}
            onChange={(val) => setField('assignReferralsTo', val)}
            isMulti={false}
            showDefaultOptions={true}
            showUserType={false}
          />
        </div>

        <label
          title="Puts the Veritas mark in the center of the QR code"
          className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground"
        >
          <input
            type="checkbox"
            checked={form.includeLogo}
            onChange={(e) => setField('includeLogo', e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          Include logo in QR code
        </label>

        <div>
          <label className={FIELD_LABEL}>Notes</label>
          <textarea
            value={form.details}
            onChange={(e) => setField('details', e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Internal notes (booth, dates, who's staffing it)"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {isEdit ? 'Save changes' : 'Create event'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};

export default EventModal;
